import { Box, Skeleton } from '@mui/material';
import { memo } from 'react';

/** Matches {@link AddCatalogItemWithQty} primary action (contained `size="small"`, height 32px). */
const ADD_BUTTON_SKELETON_WIDTH = 65;
const ADD_BUTTON_SKELETON_HEIGHT = 32;

/** Matches {@link CatalogItemCheckbox} icon (`28px` × `28px`). */
const CHECKBOX_SKELETON_SIZE = 28;

export type CatalogItemSkeletonActionVariant = 'addToOrder' | 'propertyCatalog';

export interface CatalogItemSkeletonProps {
    actionVariant?: CatalogItemSkeletonActionVariant;
}

export const CatalogItemSkeleton = memo(
    ({ actionVariant = 'propertyCatalog' }: CatalogItemSkeletonProps) => {
        const actionSlot =
            actionVariant === 'addToOrder' ? (
                <Box
                    sx={{
                        minWidth: '110px',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'flex-start',
                        flexShrink: 0,
                    }}
                >
                    <Skeleton
                        variant="rounded"
                        width={ADD_BUTTON_SKELETON_WIDTH}
                        height={ADD_BUTTON_SKELETON_HEIGHT}
                        sx={{ borderRadius: '4px', flexShrink: 0 }}
                    />
                </Box>
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'flex-start',
                        flexShrink: 0,
                    }}
                >
                    <Skeleton
                        variant="rounded"
                        width={CHECKBOX_SKELETON_SIZE}
                        height={CHECKBOX_SKELETON_SIZE}
                        sx={{ flexShrink: 0 }}
                    />
                </Box>
            );

        return (
            <Box
                sx={{
                    borderBottom: '1px solid',
                    borderColor: 'grey.500',
                }}
            >
                <Box
                    sx={{
                        padding: '20px 0',
                        minHeight: '140px',
                        boxSizing: 'border-box',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'flex-start',
                                gap: '20px',
                            }}
                        >
                            <Skeleton
                                variant="rounded"
                                width={100}
                                height={100}
                                sx={{ flexShrink: 0 }}
                            />
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexGrow: 1,
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    minHeight: '100px',
                                    minWidth: 0,
                                }}
                            >
                                <Box sx={{ minWidth: 0 }}>
                                    <Skeleton
                                        height="15px"
                                        width="92%"
                                        sx={{ marginBottom: '4px' }}
                                    />
                                    <Skeleton
                                        height="15px"
                                        width="72%"
                                        sx={{ marginBottom: 0 }}
                                    />
                                    <Skeleton
                                        height="13px"
                                        width="48%"
                                        sx={{ marginTop: '2px' }}
                                    />
                                </Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        flexShrink: 0,
                                    }}
                                >
                                    <Skeleton
                                        variant="circular"
                                        width={18}
                                        height={18}
                                    />
                                    <Skeleton height="13px" width="44%" />
                                </Box>
                            </Box>
                            {actionSlot}
                        </Box>
                    </Box>
                </Box>
            </Box>
        );
    },
);
